/**
 * „Stückliste“ auf der Seite eines Packstücks oder Lagerorts (ADR 0013 §12,
 * ADR 0017).
 *
 * Ein Knopf öffnet das PDF, im Browser erzeugt und damit auch ohne Netz. Die
 * Listen der Plätze und Packstücke darin kommen nur auf Wunsch mit --
 * standardmäßig nicht, sonst wird die Liste des LKW viele Seiten lang (so vom
 * Nutzer entschieden).
 */

import { useMemo, useState } from "react";
import { dateiOeffnen } from "../druck/ausgabe.js";
import type { Bestand } from "../lokal/bestand.js";
import { stueckliste, stuecklisten, unterorteDerListe } from "../lokal/stueckliste.js";
import { Knopf, Rueckmeldung, Schalter } from "./bausteine.jsx";

/** Höchstens so viele Namen im Hinweis des Schalters. */
const NAMEN_IM_HINWEIS = 4;

export function StuecklisteDrucken({
  bestand,
  lagerort_id,
}: {
  bestand: Bestand;
  lagerort_id: string;
}) {
  const [mitUnterlisten, setMitUnterlisten] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const liste = useMemo(() => stueckliste(bestand, lagerort_id), [bestand, lagerort_id]);
  if (liste === null) return null;

  const unter = unterorteDerListe(liste);
  const namen = unter.slice(0, NAMEN_IM_HINWEIS).map((l) => l.kennung);
  if (unter.length > NAMEN_IM_HINWEIS) namen.push("…");

  const drucken = async (): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      const { stuecklistenPdf } = await import("../druck/stueckliste.js");
      const pdf = await stuecklistenPdf(
        stuecklisten(bestand, lagerort_id, mitUnterlisten),
        bestand.basis_adresse ?? globalThis.location.origin,
        new Date(),
      );
      dateiOeffnen(pdf, `stueckliste-${liste.lagerort.kennung}.pdf`);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : String(f));
    } finally {
      setLaeuft(false);
    }
  };

  const zeilen = liste.zeilen.length;
  return (
    <section
      aria-label="Stückliste"
      className="flex flex-col gap-3 rounded-xl bg-white p-4 dark:bg-neutral-800"
    >
      <p data-testid="stueckliste-umfang">
        {zeilen === 0
          ? "Gerade ist nichts darin gebucht — die Liste hätte nur den Kopf."
          : `Aktueller Inhalt: ${String(liste.teile)} ${liste.teile === 1 ? "Teil" : "Teile"} in ${String(zeilen)} ${zeilen === 1 ? "Zeile" : "Zeilen"}.`}
      </p>
      {unter.length > 0 && (
        <Schalter
          beschriftung="Mit den Listen darin"
          an={mitUnterlisten}
          aendern={setMitUnterlisten}
          hinweis={`${namen.join(", ")} — jede auf einer neuen Seite.`}
        />
      )}
      <Knopf art="haupt" disabled={laeuft} onClick={() => void drucken()}>
        {laeuft ? "wird erzeugt …" : "Stückliste als PDF"}
      </Knopf>
      {fehler !== null && (
        <Rueckmeldung art="warnung" kopf="Das PDF ließ sich nicht erzeugen" zusatz={fehler} />
      )}
    </section>
  );
}
